import { NavLink, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { PiMagicWandDuotone, PiChatTextDuotone } from "react-icons/pi";
import ThemedSkeleton from "./ThemedSkeleton";
import Session from "supertokens-auth-react/recipe/session";
import { PermissionClaim } from "supertokens-auth-react/recipe/userroles";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Calendar, File, Route, Search, Star, User } from "lucide-react";
import ManageBillingButton from "@/features/settings/ManageBillingButton";

const NavList = styled.ul`
  flex-direction: column;
  margin-top: 0.5rem;

  & span {
    display: ${(props) => (props.$expanded ? "flex" : "none")};
  }
`;

const StyledNavLink = styled(NavLink)`
  margin-bottom: 0.5rem;

  &:link,
  &:visited {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    justify-content: ${(props) => (props.$expanded ? "left" : "center")};

    color: var(--gray-600);
    font-size: 1.2rem;

    font-weight: 500;
    padding: 0.5rem 0.4rem;
    transition: all 0.3s;
    white-space: nowrap;
    overflow: hidden;
  }

  /* This works because react-router places the active class on the active NavLink */
  &:hover,
  &:active,
  &.active:link,
  &.active:visited {
    color: var(--brand-600);
    background-color: var(--gray-100);
    border-radius: var(--border-radius-sm);
    border-color: var(--gray-200);
  }

  & svg {
    height: 1.4rem;
    width: auto;
    color: var(--gray-400);
    transition: all 0.3s;
  }

  &:hover svg,
  &:active svg,
  &.active:link svg,
  &.active:visited svg {
    color: var(--brand-600);
  }
  /* Disable styles */
  ${(props) =>
    props.$disabled &&
    `
    opacity: 0.5;
    cursor: default;
  `}
`;

function UpgradeTooltipContent() {
  return (
    <TooltipContent side="right">
      <ManageBillingButton
        variant="link"
        text="Upgrade"
        className="text-sm p-0 h-full"
      />
    </TooltipContent>
  );
}

function TooltipLink({
  title,
  icon: Icon,
  to,
  toFn,
  content,
  isProposalFeature,
  expanded,
}) {
  const navigate = useNavigate();

  let claimValue = Session.useClaimValue(PermissionClaim);
  if (claimValue.loading || !claimValue.doesSessionExist) {
    return null;
  }
  let permissions = claimValue.value;
  const isMissingProposalPermission =
    isProposalFeature &&
    !(Array.isArray(permissions) && permissions.includes("proposal"));

  const handleClick = (e) => {
    if (isMissingProposalPermission) {
      e.preventDefault();
    } else if (toFn) {
      // Prevent default navigation and programmatically navigate with a new UUID
      e.preventDefault();
      navigate(toFn());
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <StyledNavLink
            $expanded={expanded}
            to={to || ""} // Ensure new UUID on each click
            $disabled={isMissingProposalPermission}
            onClick={handleClick}
          >
            <Icon />
            <span> {title} </span>
          </StyledNavLink>
        </TooltipTrigger>
        {isMissingProposalPermission ? (
          <UpgradeTooltipContent />
        ) : (
          <TooltipContent side="right">
            <p>{content}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

function MainNav({ expanded, isLoading }) {
  const links = [
    {
      title: "Search",
      icon: Search,
      tooltipContent: "Search",
      to: "/search",
    },
    {
      title: "Company Profile",
      icon: User,
      tooltipContent: "Company Profile",
      to: "/business_profile",
    },
    {
      title: "Chat with documents",
      icon: PiChatTextDuotone,
      tooltipContent: "Chat with Your documents",
      to: `/chat`,
    },
    {
      title: "Schedule Bid Alerts",
      icon: Calendar,
      tooltipContent: "Schedule Bid Alerts",
      to: "/schedule",
    },
    {
      title: "Saved Opportunities",
      icon: Star,
      tooltipContent: "Saved Opportunities",
      to: "/leads",
    },
    {
      title: "Journey Hub",
      icon: Route,
      tooltipContent: "Journey Hub",
      to: "/journey-hub",
    },
    {
      title: "Proposals",
      icon: File,
      tooltipContent: "Proposals",
      to: "/proposals",
      isProposalFeature: true,
    },
  ];
  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <ThemedSkeleton className="h-10 w-5/6" />
        <ThemedSkeleton className="h-10" />
        <ThemedSkeleton className="h-10" />
        <ThemedSkeleton className="h-10 w-5/6" />
        <ThemedSkeleton className="h-10" />
        <ThemedSkeleton className="h-10 w-5/6" />
      </div>
    );
  }
  return (
    <nav>
      <NavList $expanded={expanded}>
        {links.map((link) => (
          <TooltipLink
            key={link.title}
            title={link.title}
            icon={link.icon}
            to={link.to}
            toFn={link.toFn}
            content={link.tooltipContent}
            isProposalFeature={link.isProposalFeature}
            expanded={expanded}
          />
        ))}
      </NavList>
    </nav>
  );
}

export default MainNav;
